import './CompleteCourseForm.scss'
import React, {useContext, useEffect, useState} from "react";
import {I18nReactContext} from "../../contexts/I18nContext";
import {PageTitle} from "../PageTitle/PageTitle";
import {completeCourseAPI, getSignDocApi} from "../../Services/api";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import {get} from "lodash";
import {Navigate} from "react-router-dom";
import {Confetti} from "../Smart/Confetti/Confetti";
import {isSignDocComplete} from "../SignDoc/SignDocLib";
import {removeStartedCourse, resolveStartedCourseId} from "../../Utils/Course/startedCourses";

export const CompleteCourseForm = () => {
    const [isLoading, setIsLoading] = React.useState(false)
    const {i18n} = useContext(I18nReactContext);
    const [signDoc, setSignDoc] = React.useState({})
    const [youGotLost, setYouGotLost] = React.useState(false)
    const [showConfetti, setShowConfetti] = React.useState(false)

    const [learningPathRootId, setLearningPathRootId] = useState(null);
    const [shouldNavigate, setShouldNavigate] = useState(false);

    /**
     * Loads the finished course. The course package sends a fixed ID (the version it was uploaded for),
     * so we resolve it to the version the user actually opened before loading or completing anything.
     * @returns {Promise<void>}
     */
    const init = async () => {
        setIsLoading(true)
        const courseIdFromLocalStorage = typeof window !== undefined && window.localStorage.getItem("CompletedCourse")
        if (!courseIdFromLocalStorage) {
            setYouGotLost(true)
            setIsLoading(false)
            return
        }
        const signDocId = resolveStartedCourseId({courseIdFromUrl: courseIdFromLocalStorage});
        // First retrieve and set the learning path ID
        let learningPathId = null;
        try {
            const mappingJson = window.localStorage.getItem('signDocToLearningPathMap');
            if (mappingJson) {
                const mapping = JSON.parse(mappingJson);
                learningPathId = mapping[signDocId];
                if (learningPathId) {
                    setLearningPathRootId(learningPathId);
                    window.localStorage.removeItem('signDocToLearningPathMap', learningPathId);
                }
            }
        } catch (e) {
            console.error('Error reading learningPath mapping from localStorage', e);
        }

        window !== undefined && window.localStorage.removeItem("CompletedCourse");
        const newSignDoc = await getSignDocApi({signDocId});
        setSignDoc(newSignDoc);
        setIsLoading(false);
        // If doNotForceCompletion flag and not part of a learning path, mark as completed to redirect
        if (get(newSignDoc, 'doNotForceCompletion') && !learningPathId) {
            setShouldNavigate(true);
        }
    }
    useEffect(() => {
        init()
    }, []);

    useEffect(() => {
        if (signDoc.id && !youGotLost) {
            confirmCourseCompletion();
        }
    }, [signDoc, youGotLost]);

    // After confetti, transition to completed
    useEffect(() => {
        if (showConfetti) {
            const timer = setTimeout(() => {
                setShouldNavigate(true);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [showConfetti]);

    /**
     * Completes the course on the server, then forgets it from the started courses map.
     * The rest layer resolves (not rejects) on a server error, so the entry is removed only
     * when the returned course is complete. On failure it stays, so a retry still resolves to the same version.
     * @returns {Promise<void>}
     */
    const confirmCourseCompletion = async () => {
        setIsLoading(true)
        try {
            const completedCourse = await completeCourseAPI({signDocId: signDoc.id})
            setIsLoading(false)
            if (isSignDocComplete({ signDoc: completedCourse })) {
                removeStartedCourse({signDocId: signDoc.id});
                setShowConfetti(true)
            } else {
                setShouldNavigate(true);
            }
        } catch (error) {
            console.error('Error completing course', error);
            setIsLoading(false)
        }
    }

    if (shouldNavigate) {
        const to = learningPathRootId
            ? `/learningPathViewer/${learningPathRootId}`
            : '/learningCenter/myContent';
        return <Navigate to={to} replace />;
    }

    return <div className="CompleteCourseForm MenuPage">
        {isLoading && <LoadingSpinner/>}
        {!youGotLost ? <PageTitle
            title={i18n.translate('completedCoursePage.title')}
            subTitle={i18n.translate('completedCoursePage.finishedSuccessfully')}
            subTitleBold={signDoc.docName}/> :
            <PageTitle
                title={i18n.translate('completedCoursePage.oops')}
                subTitle={i18n.translate('completedCoursePage.youGotLost')}/>
        }
        <Confetti showConfetti={showConfetti}/>
    </div>
}